import { Link } from "react-router-dom";
import { Award, Users, ThumbsUp, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";
import { getOptimizedImageUrl } from "@/lib/imageUtils";
import type { TrustIndicator as TrustIndicatorData } from "@/hooks/useGlobalSettings";

interface TrustIndicator {
  icon: React.ElementType;
  value: string;
  label: string;
}

interface TrustSectionProps {
  ownerName?: string;
  ownerTitle?: string;
  ownerBio?: string;
  ownerImage?: string;
  indicators?: TrustIndicatorData[];
  ctaText?: string;
  ctaLink?: string;
  backgroundColor?: string;
  textColor?: string;
  badgeValue?: string;
  badgeLabel?: string;
}

const defaultIndicators: TrustIndicator[] = [
  { icon: Calendar, value: "6+", label: "Years Experience" },
  { icon: Users, value: "500+", label: "Happy Customers" },
  { icon: ThumbsUp, value: "100%", label: "Satisfaction Rate" },
  { icon: Award, value: "5★", label: "Google Rating" },
];

// Map indicator data to include icons
const mapIndicators = (data?: TrustIndicatorData[]): TrustIndicator[] => {
  if (!data || data.length === 0) return defaultIndicators;
  const icons = [Calendar, Users, ThumbsUp, Award];
  return data.map((item, index) => ({
    icon: icons[index % icons.length],
    value: item.value,
    label: item.label,
  }));
};

const TrustSection = ({
  ownerName,
  ownerTitle,
  ownerBio,
  ownerImage,
  indicators,
  ctaText = "Learn More About Us",
  ctaLink = "/about",
  backgroundColor,
  textColor,
  badgeValue,
  badgeLabel,
}: TrustSectionProps) => {
  const { settings } = useSettings();
  
  const displayIndicators = mapIndicators(indicators);
  
  const name = ownerName || settings?.site_name || "MyClean Exteriors";
  const title = ownerTitle || "Owner & Lead Technician";
  const bio = ownerBio || "With over 6 years of experience in exterior cleaning, we've built our reputation on quality workmanship, honest pricing, and treating every property like our own. As a local business, we take pride in helping our neighbours maintain beautiful, safe outdoor spaces.";
  const image = ownerImage || settings?.about_hero_image;
  const textStyle = textColor ? { color: textColor } : undefined;

  return (
    <section 
      className={`section-padding ${!backgroundColor ? 'bg-muted/30' : ''}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="container-wide">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <div className="relative">
            {image ? (
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/10 rounded-2xl -z-10 transform rotate-3" />
                <img
                  src={getOptimizedImageUrl(image, 'hero-desktop')}
                  alt={name}
                  className="rounded-2xl shadow-2xl w-full max-h-[500px] object-cover"
                  loading="lazy"
                />
                {/* Floating trust badge */}
                {(badgeValue || badgeLabel) && (
                  <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-4 rounded-xl shadow-lg hidden md:block">
                    <div className="text-center">
                      <span className="text-3xl font-bold block">{badgeValue || "6+"}</span>
                      <span className="text-sm opacity-90">{badgeLabel ? badgeLabel.split('\n').map((line, i) => <span key={i}>{line}<br /></span>) : "Years Serving the Community"}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl max-h-[500px] flex items-center justify-center">
                <div className="text-center text-primary-foreground p-8">
                  <Award className="h-16 w-16 mx-auto mb-4 opacity-80" />
                  <p className="text-lg font-heading font-semibold">Your Trusted Local Team</p>
                </div>
              </div>
            )}
          </div>

          {/* Content Side */}
          <div className="space-y-6">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
                Meet Your Local Experts
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2" style={textStyle}>
                {name}
              </h2>
              <p className="text-lg text-primary font-medium" style={textStyle}>{title}</p>
            </div>

            <p className="text-muted-foreground text-lg leading-relaxed" style={textStyle}>
              {bio}
            </p>

            {/* Trust Indicators Grid */}
            <div className="grid grid-cols-2 gap-4 py-6">
              {displayIndicators.map((indicator, index) => {
                const Icon = indicator.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-background rounded-xl border border-border hover:border-primary/30 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <span className="text-2xl font-bold text-foreground block leading-tight">
                        {indicator.value}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {indicator.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button asChild size="lg" className="group">
              <Link to={ctaLink}>
                {ctaText}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
